/**
 * ScnLinkDrawer (S1.4, extended S7.3) — retroactive SCN Link/Join drawer,
 * now carrying the board-approved 選擇式關聯 selection list.
 *
 * ONE searchable five-column list (既有產品公司料號 / OEM所屬國家市場 /
 * OEM Number / SCN / NOTE) covering BOTH link paths, exactly as the OEM/SCN
 * board's worked example draws it (linked 產品A/產品F next to not-yet-linked
 * 產品C/產品D):
 * - selecting a row WITH an SCN = join that group (spec-master context +
 *   link to the Spec Master detail rendered below);
 * - selecting a row WITHOUT one = create a new group with that partner
 *   (both sides get the code; entry is permission-aware — auto-number via
 *   `nextScnCode` for everyone, manual 自由編輯 only with 產品管理 permission,
 *   hidden otherwise — hide-don't-disable).
 *
 * Overflow spec (ux-lens): minimum drawer width declared
 * (`SCN_LINK_DRAWER_MIN_WIDTH`); truncation priority is explicit — NOTE
 * ellipsizes first (the only flexible column), then OEM所屬國家市場, then
 * 既有產品公司料號; SCN and OEM Number get the widest fixed columns so they
 * truncate last. Every ellipsized cell exposes its full value on tooltip.
 * Empty-candidates and search-no-results are DISTINCT declared states.
 *
 * SCN is optional and ALWAYS retroactive — linking (and later unlinking)
 * never rewrites past documents; they keep their own references.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Button, Drawer, Input, Radio, Spin, Table, Tooltip, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { LinkOutlined, RightOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PurpleTag from '@/components/common/PurpleTag';
import SearchInput from '@/components/common/SearchInput';
import { ListEmptyState } from '@/components/common/TableContainer';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useErrorHandler } from '@/lib/errors/useErrorHandler';
import { can } from '@/lib/rbac/principal';
import { getUserProfile } from '@/services/api/axios.config';
import { Permission } from '@/models/user';
import type { User } from '@/models/user';
import {
  matchesScnLinkRow,
  nextScnCode,
  scnCodeExists,
  useProductMutations,
  useScnGroups,
  useScnGroupSpec,
  useScnLinkCandidateRows,
} from '@/hooks/useProducts';
import type { ScnLinkCandidateRow } from '@/hooks/useProducts';
import type { Product } from '@/types/api/product';
import { scnLinkSchema } from './schemas';
import type { ScnLinkFormData } from './schemas';

const { Text } = Typography;

/* eslint-disable react-refresh/only-export-components -- the ux-lens overflow
   contract (min width / truncation priority / column registry) is DATA the
   S7.3 tests assert against (the PRICING_SECTION_MOUNT precedent). */

/**
 * Minimum drawer width for the five-column selection list (ux-lens). 720 is
 * the established wide-drawer width (NetSuiteImportDrawer precedent).
 */
export const SCN_LINK_DRAWER_MIN_WIDTH = 720;

/**
 * Explicit truncation priority (ux-lens): first entry ellipsizes first.
 * NOTE is the ONLY flexible column (absorbs squeeze first); market and 料號
 * get the tighter fixed widths; SCN and OEM Number the widest — last to clip.
 */
export const SCN_LINK_TRUNCATION_PRIORITY = ['note', 'market', 'sku', 'scn', 'oemNo'] as const;

/**
 * Column registry (registry-as-data): the board's five columns in board
 * order. `width: undefined` marks the flexible column — exactly one, and it
 * must be the FIRST entry of SCN_LINK_TRUNCATION_PRIORITY.
 */
export const SCN_LINK_COLUMNS: ReadonlyArray<{
  key: (typeof SCN_LINK_TRUNCATION_PRIORITY)[number];
  titleKey: string;
  width: number | undefined;
}> = [
  { key: 'sku', titleKey: 'products:detail.scn.drawer.columns.sku', width: 148 },
  { key: 'market', titleKey: 'products:detail.scn.drawer.columns.market', width: 96 },
  { key: 'oemNo', titleKey: 'products:detail.scn.drawer.columns.oemNo', width: 150 },
  { key: 'scn', titleKey: 'products:detail.scn.drawer.columns.scn', width: 112 },
  { key: 'note', titleKey: 'products:detail.scn.drawer.columns.note', width: undefined },
];

/* eslint-enable react-refresh/only-export-components */

/** Full value on hover for every ellipsized cell (ux-lens tooltip rule). */
const CellWithTooltip = ({ full, children }: { full: string; children: ReactNode }) => (
  <Tooltip title={full} placement="topLeft">
    <span
      style={{
        display: 'block',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      {children}
    </span>
  </Tooltip>
);

interface ScnLinkDrawerProps {
  open: boolean;
  product: Product;
  onClose: () => void;
  onLinked: () => void;
}

export const ScnLinkDrawer = ({ open, product, onClose, onLinked }: ScnLinkDrawerProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useErrorHandler();
  const { linkScn, linkPartnerScn } = useProductMutations(product.id);

  const { groups, isLoading: groupsLoading, refresh: refreshGroups } = useScnGroups();
  const { rows, isLoading: rowsLoading } = useScnLinkCandidateRows(product);

  // 自由編輯 SCN 編碼權限 (board green diamond, products.manage build-default
  // Q-O1) — manual entry is HIDDEN without it, never disabled. Canonical
  // Axis-A read over the stored profile snapshot (the `canEditCostBasis`
  // precedent) — NOT the legacy `usePermissions` redux slice, which is never
  // populated under rbacV2.
  const canEditScnCode = can(getUserProfile() as User | null, Permission.MANAGE_PRODUCTS);

  const [search, setSearch] = useState('');
  const [selectedRowId, setSelectedRowId] = useState<string>();
  const [codeEntryMode, setCodeEntryMode] = useState<'auto' | 'manual'>('auto');
  const [manualCode, setManualCode] = useState('');
  const [manualCodeError, setManualCodeError] = useState<string>();

  const defaultFormValues = useMemo<ScnLinkFormData>(
    () => ({ mode: 'join', scn: undefined, partnerProductId: undefined }),
    []
  );

  const {
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<ScnLinkFormData>({
    resolver: zodResolver(scnLinkSchema),
    defaultValues: defaultFormValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (!open) return;
    reset(defaultFormValues);
    setSearch('');
    setSelectedRowId(undefined);
    setCodeEntryMode('auto');
    setManualCode('');
    setManualCodeError(undefined);
  }, [open, defaultFormValues, reset]);

  const suggestedCode = useMemo(() => nextScnCode(groups), [groups]);

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesScnLinkRow(row, search)),
    [rows, search]
  );

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectedRowId),
    [rows, selectedRowId]
  );

  // Join-path spec-master context — fetched only once a linked row is picked.
  const { group: specGroup, isLoading: specLoading } = useScnGroupSpec(selectedRow?.scn);

  // 選擇式關聯: the selected row DERIVES the link mode (board 最佳解法 —
  // selection replaces memory-and-retype; no separate mode radio).
  const selectRow = (row: ScnLinkCandidateRow) => {
    setSelectedRowId(row.id);
    setManualCodeError(undefined);
    if (row.scn) {
      setValue('mode', 'join');
      setValue('scn', row.scn);
      setValue('partnerProductId', undefined);
    } else {
      setValue('mode', 'create');
      setValue('partnerProductId', row.id);
      setValue('scn', undefined);
    }
    void trigger(['scn', 'partnerProductId']);
  };

  const resolveCreateCode = (): string | null => {
    if (!canEditScnCode || codeEntryMode === 'auto') return suggestedCode;
    const code = manualCode.trim();
    if (!code) {
      setManualCodeError(t('products:validation.scnCodeRequired'));
      return null;
    }
    if (scnCodeExists(groups, code) || (product.scn ?? '').toLowerCase() === code.toLowerCase()) {
      setManualCodeError(t('products:validation.scnCodeTaken'));
      return null;
    }
    return code;
  };

  const onSubmit = async (data: ScnLinkFormData) => {
    try {
      if (data.mode === 'join' && data.scn) {
        await linkScn(data.scn);
      } else if (data.mode === 'create' && data.partnerProductId) {
        const code = resolveCreateCode();
        if (!code) return;
        // Link both sides to the new group (partner first, then self)
        await linkPartnerScn(data.partnerProductId, code);
        await linkScn(code);
      }
      // AWAIT the groups refetch while the drawer is still mounted: the
      // response writes the shared '/api/products-options?hasScn=true'
      // cache, which pushes fresh members into the page's useScnGroups
      // instance (same cacheKey). Fire-and-forget here left the section's
      // member read-back stale until remount.
      await refreshGroups();
      handleSuccess(t('products:detail.scn.linkedMessage'));
      onLinked();
      onClose();
    } catch (error) {
      handleError(error);
    }
  };

  const renderCell = (row: ScnLinkCandidateRow, key: (typeof SCN_LINK_COLUMNS)[number]['key']) => {
    switch (key) {
      case 'sku':
        return (
          <CellWithTooltip full={`${row.sku} · ${row.internalProductId}`}>
            <span style={{ fontWeight: token.fontWeightStrong }}>{row.sku}</span>
            <span
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextSecondary,
                marginLeft: '6px',
              }}
            >
              {row.internalProductId}
            </span>
          </CellWithTooltip>
        );
      case 'market':
        return <CellWithTooltip full={row.market}>{row.market}</CellWithTooltip>;
      case 'oemNo':
        return row.oemNo ? (
          <CellWithTooltip full={row.oemNo}>{row.oemNo}</CellWithTooltip>
        ) : (
          '-'
        );
      case 'scn':
        return row.scn ? (
          <CellWithTooltip full={row.scn}>
            <PurpleTag>{row.scn}</PurpleTag>
          </CellWithTooltip>
        ) : (
          '-'
        );
      case 'note':
        return row.note ? (
          <CellWithTooltip full={row.note}>{row.note}</CellWithTooltip>
        ) : (
          '-'
        );
    }
  };

  const columns: TableProps<ScnLinkCandidateRow>['columns'] = SCN_LINK_COLUMNS.map(
    ({ key, titleKey, width }) => ({
      title: t(titleKey),
      key,
      width,
      ellipsis: { showTitle: false },
      render: (_: unknown, row: ScnLinkCandidateRow) => renderCell(row, key),
    })
  );

  // DISTINCT declared list states (ux-lens): no candidates at all vs a
  // search that matched nothing — never a blank list body.
  const emptyState =
    rows.length === 0 ? (
      <ListEmptyState
        icon={<LinkOutlined />}
        title={t('products:detail.scn.drawer.emptyCandidates')}
        description={t('products:detail.scn.drawer.emptyCandidatesDetail')}
      />
    ) : (
      <ListEmptyState
        title={t('products:detail.scn.drawer.searchNoResults')}
        description={t('products:detail.scn.drawer.searchNoResultsDetail')}
      />
    );

  const selectionError = errors.scn ?? errors.partnerProductId;

  return (
    <Drawer
      title={t('products:detail.scn.drawerTitle')}
      open={open}
      onClose={onClose}
      width={SCN_LINK_DRAWER_MIN_WIDTH}
      destroyOnHidden
      footer={
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button onClick={onClose} style={{ height: `${controlHeightTokens.controlHeightXL}px` }}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('products:detail.scn.linkAction')}
          </Button>
        </div>
      }
    >
      <Alert
        className="tw-mb-4"
        type="info"
        showIcon
        message={t('products:detail.scn.retroNote')}
      />

      <div style={formStyles.fieldContainerStyle}>
        <Text strong style={formStyles.sectionTitleStyle}>
          {t('products:detail.scn.drawer.selectTitle')}
        </Text>
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: '12px' }}
        >
          {t('products:detail.scn.drawer.selectHint')}
        </Text>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('products:detail.scn.drawer.searchPlaceholder')}
          width="100%"
          className="tw-mb-3"
        />
        <Table<ScnLinkCandidateRow>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          loading={rowsLoading || groupsLoading}
          dataSource={filteredRows}
          columns={columns}
          pagination={false}
          scroll={{ y: 320 }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedRowId ? [selectedRowId] : [],
            onSelect: selectRow,
            columnWidth: 40,
          }}
          onRow={(row) => ({
            onClick: () => selectRow(row),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: emptyState }}
        />
        {selectionError && !selectedRow && (
          <Text style={formStyles.errorStyle}>
            {t('products:detail.scn.drawer.selectionRequired')}
          </Text>
        )}
      </div>

      {selectedRow?.scn && (
        <div
          style={{
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: `${radiusTokens.card}px`,
            padding: '16px',
            backgroundColor: token.colorFillQuaternary,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Text strong>{t('products:detail.scn.drawer.specContext')}</Text>
              <PurpleTag>{selectedRow.scn}</PurpleTag>
            </div>
            <Button
              type="link"
              size="small"
              icon={<RightOutlined />}
              iconPosition="end"
              onClick={() => {
                onClose();
                navigate(`/auth/products/scn/${selectedRow.scn}`);
              }}
            >
              {t('products:detail.scn.drawer.viewSpecMaster')}
            </Button>
          </div>
          {specLoading ? (
            <Spin size="small" className="tw-mt-2" />
          ) : (
            <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-1">
              <Text style={{ fontSize: token.fontSizeSM }}>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, marginRight: '6px' }}>
                  {t('products:detail.scn.drawer.specName')}
                </Text>
                {specGroup?.specName ?? '-'}
              </Text>
              <Text style={{ fontSize: token.fontSizeSM }}>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, marginRight: '6px' }}>
                  {t('products:detail.scn.drawer.majorCategory')}
                </Text>
                {specGroup?.majorCategory ?? '-'}
              </Text>
              <Text style={{ fontSize: token.fontSizeSM }}>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM, marginRight: '6px' }}>
                  {t('products:detail.scn.drawer.memberCount')}
                </Text>
                {specGroup?.memberCount ?? '-'}
              </Text>
            </div>
          )}
        </div>
      )}

      {selectedRow && !selectedRow.scn && (
        <div style={formStyles.fieldContainerStyle}>
          <Text strong style={formStyles.sectionTitleStyle}>
            {t('products:detail.scn.newCode')}
          </Text>
          {canEditScnCode && (
            <Radio.Group
              value={codeEntryMode}
              onChange={(e) => {
                setCodeEntryMode(e.target.value as 'auto' | 'manual');
                setManualCodeError(undefined);
              }}
              className="tw-mb-3"
              style={{ display: 'block' }}
            >
              <Radio value="auto">{t('products:detail.scn.drawer.codeEntryAuto')}</Radio>
              <Radio value="manual">{t('products:detail.scn.drawer.codeEntryManual')}</Radio>
            </Radio.Group>
          )}
          {canEditScnCode && codeEntryMode === 'manual' ? (
            <>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('products:detail.scn.drawer.manualCodeLabel')}
              </Text>
              <Input
                size="large"
                value={manualCode}
                status={manualCodeError ? 'error' : undefined}
                placeholder={t('products:detail.scn.drawer.manualCodePlaceholder')}
                style={formStyles.inputStyle}
                onChange={(e) => {
                  setManualCode(e.target.value);
                  setManualCodeError(undefined);
                }}
              />
              {manualCodeError && <Text style={formStyles.errorStyle}>{manualCodeError}</Text>}
            </>
          ) : (
            <div className="tw-flex tw-items-center tw-gap-2">
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('products:detail.scn.drawer.codeEntryAuto')}
              </Text>
              <PurpleTag>{suggestedCode}</PurpleTag>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ScnLinkDrawer;
